import type { GoodStock, GoodType, Shipment } from "@cwb/core";

/**
 * What the broker owns, as opposed to what has landed.
 *
 * Stock still on the road toward the hub counts: the money left the account the
 * moment the order was signed and the goods are not coming back. A figure that
 * showed only landed stock made a player who had just spent $300,000 look at an
 * unchanged number and buy again — and the core agrees, `executeBrokerSell`
 * draws on the pickup convoys too.
 *
 * Pure and separate from any component because two places read it and one of
 * them decides whether the player may act at all: the sell gate in TradePanel
 * and the holdings the same panel prints. If those two ever disagreed, a button
 * would be enabled for stock the server would refuse to move.
 */
export interface Holding {
  /** Landed plus inbound — what the broker owns. */
  total: number;
  /** The part of it that is still a truck. */
  inbound: number;
}

/**
 * Only `TO_BROKER` convoys count. A `TO_NATION` shipment has the same shape and
 * the broker's name is on it, but that stock has already been sold and counting
 * it would show goods the player no longer owns.
 */
export function holdingOf(
  good: GoodType,
  warehouse: GoodStock,
  shipments: readonly Shipment[],
): Holding {
  let inbound = 0;
  for (const shipment of shipments) {
    if (shipment.kind === "TO_BROKER" && shipment.goodType === good) inbound += shipment.quantity;
  }
  return { total: Math.floor(warehouse[good] + inbound), inbound: Math.round(inbound) };
}
